import { useEffect, useRef } from 'react';

interface Instance {
  el: HTMLElement;
  strength: number;
  threshold: number;
}

const instances = new Map<symbol, Instance>();
let mouseX = -9999;
let mouseY = -9999;
let rafId: number | null = null;
let isListening = false;
let mouseMoved = false;

const tick = () => {
  rafId = null;

  if (!mouseMoved) return;
  mouseMoved = false;

  instances.forEach(({ el, strength, threshold }) => {
    const rect = el.getBoundingClientRect();
    const elX = rect.left + rect.width / 2;
    const elY = rect.top + rect.height / 2;
    const distX = mouseX - elX;
    const distY = mouseY - elY;
    const distance = Math.hypot(distX, distY);

    if (distance < threshold) {
      const targetX = distX * strength;
      const targetY = distY * strength;
      el.style.transform = `translate3d(${targetX}px, ${targetY}px, 0)`;
      el.style.transition = 'transform 0.1s ease-out';
    } else {
      el.style.transform = 'translate3d(0px, 0px, 0)';
      el.style.transition = 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)';
    }
  });

  scheduleRAF();
};

const scheduleRAF = () => {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(tick);
};

const cancelRAF = () => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
};

const handleMouseMove = (e: MouseEvent) => {
  mouseX = e.clientX;
  mouseY = e.clientY;
  mouseMoved = true;

  scheduleRAF();
};

const startListening = () => {
  if (isListening) return;
  isListening = true;
  window.addEventListener('mousemove', handleMouseMove, { passive: true });
};

const stopListening = () => {
  if (!isListening) return;
  isListening = false;
  window.removeEventListener('mousemove', handleMouseMove);
  cancelRAF();
};

export const useMagnetic = <T extends HTMLElement = HTMLElement>(
  strength = 0.35,
  threshold = 45
) => {
  const ref = useRef<T>(null);
  const keyRef = useRef<symbol>(Symbol('magnetic'));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    const instanceKey = keyRef.current;
    instances.set(instanceKey, { el, strength, threshold });
    startListening();

    const handleMouseLeave = () => {
      el.style.transform = 'translate3d(0px, 0px, 0)';
      el.style.transition = 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)';
    };
    el.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      el.removeEventListener('mouseleave', handleMouseLeave);
      instances.delete(instanceKey);

      if (instances.size === 0) {
        stopListening();
      }
    };
  }, [strength, threshold]);

  return ref;
};

export default useMagnetic;
